import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITY } from "@/lib/mock";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Sparkles, Loader2, Wand2, Paperclip, X, ImageUp, GraduationCap } from "lucide-react";

// A student's profile is "complete" once these are all on file. Staff/other
// accounts are never gated — this is purely the USN/semester/section a
// student needs identified before their first ticket.
const profileComplete = (u) =>
  !!(u?.name && u?.usn && u?.department && u?.semester && u?.section && u?.phone && u?.email);

export default function NewTicketDialog({ categories = [], onCreated, trigger }) {
  const { user, updateProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const needsProfile = user?.user_type === "student" && !profileComplete(user);

  // ── One-time student profile (name, USN, department, semester, section,
  // mobile, email) — collected once, before the first ticket can be raised.
  const [pf, setPf] = useState({ name: "", usn: "", department: "", semester: "", section: "", phone: "", email: "" });
  const [pfBusy, setPfBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPf({
      name: user?.name || "", usn: user?.usn || "", department: user?.department || "",
      semester: user?.semester || "", section: user?.section || "",
      phone: user?.phone || "", email: user?.email || "",
    });
  }, [open, user]);
  const setPfField = (k) => (e) => setPf((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const savePf = async (e) => {
    e.preventDefault();
    if (!pf.name.trim()) return toast.error("Name is required");
    if (!pf.usn.trim()) return toast.error("USN is required");
    if (!pf.department.trim()) return toast.error("Department is required");
    if (!pf.semester) return toast.error("Semester is required");
    if (!pf.section.trim()) return toast.error("Section is required");
    if (!pf.phone.trim()) return toast.error("Mobile number is required");
    if (!pf.email.trim()) return toast.error("Email is required");
    setPfBusy(true);
    try {
      await updateProfile({
        name: pf.name.trim(), usn: pf.usn.trim(), department: pf.department.trim(),
        semester: pf.semester, section: pf.section.trim().toUpperCase(),
        phone: pf.phone.trim(), email: pf.email.trim(),
      });
      toast.success("Profile saved — you can raise your ticket now");
    } catch (err) { toast.error(err.message || "Could not save your profile"); }
    finally { setPfBusy(false); }
  };

  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("");
  const [subCat, setSubCat] = useState("");
  const [pr, setPr] = useState("medium");
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [anonContact, setAnonContact] = useState("");

  // ── Optional image attachment (uploaded to Google Drive once the ticket exists) ──
  const [image, setImage] = useState(null); // File
  const [imagePreview, setImagePreview] = useState(null); // object URL
  const [imgUploading, setImgUploading] = useState(false);

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Only image files are allowed."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB."); return; }
    setImage(file);
    setImagePreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(file); });
  };
  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(null); setImagePreview(null);
  };

  // ── AI Assist ──
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiStage, setAiStage] = useState("");
  const [aiResult, setAiResult] = useState(null); // { reasoning, confidence }
  const [aiAnswer, setAiAnswer] = useState(null);  // KB fix text
  const [aiSources, setAiSources] = useState([]);

  // ── Similar-ticket check (v8.1) — new state only, doesn't touch anything above ──
  const [similarBusy, setSimilarBusy] = useState(false);
  const [similarChecked, setSimilarChecked] = useState(false); // has this exact draft been checked yet
  const [similarMatches, setSimilarMatches] = useState(null);  // [] once checked clean, null = not yet checked
  const [watchBusy, setWatchBusy] = useState(null); // id of the ticket currently being subscribed to

  // Editing the draft after a check invalidates it, so the next submit re-checks.
  useEffect(() => { setSimilarChecked(false); setSimilarMatches(null); }, [title, desc, cat]);
  // Closing the dialog clears any pending similar-tickets panel for next time.
  useEffect(() => { if (!open) { setSimilarChecked(false); setSimilarMatches(null); } }, [open]);

  const fillFromDraft = (d, incident) => {
    setTitle(d.title || "");
    setTimeout(() => d.category_id && setCat(String(d.category_id)), 220);
    setTimeout(() => d.sub_category_id && setSubCat(String(d.sub_category_id)), 480);
    setTimeout(() => setPr(d.priority || "medium"), 640);
    setTimeout(() => setDesc(d.description || incident), 300);
    setAiResult({ reasoning: d.reasoning, confidence: d.confidence, catName: d.category_name, subName: d.sub_category_name, priority: d.priority });
  };

  const runTriage = async () => {
    const incident = aiText.trim();
    if (incident.length < 8) { toast.error("Tell me a bit more about the problem"); return; }
    setAiBusy(true); setAiResult(null); setAiAnswer(null);
    const stages = ["Understanding the issue…", "Matching a service…", "Judging priority…", "Drafting the ticket…"];
    let si = 0; setAiStage(stages[0]);
    const iv = setInterval(() => { si = (si + 1) % stages.length; setAiStage(stages[si]); }, 900);
    try {
      const r = await api.aiTriage(incident);
      if (!r?.draft) throw new Error("Couldn't read that — try rephrasing.");
      fillFromDraft(r.draft, incident);
    } catch (e) { toast.error(e.message || "AI could not draft this ticket"); }
    finally { clearInterval(iv); setAiBusy(false); setAiStage(""); }
  };

  // Deflection-first: try a KB fix; if none, draft the ticket automatically.
  const runAssist = async () => {
    const incident = aiText.trim();
    if (incident.length < 8) { toast.error("Tell me a bit more about the problem"); return; }
    setAiBusy(true); setAiResult(null); setAiAnswer(null);
    setAiStage("Searching known fixes…");
    try {
      const s = await api.aiSolve(incident);
      if (s?.answer) { setAiAnswer(s.answer); setAiSources(s.sources || []); setAiBusy(false); setAiStage(""); return; }
    } catch (e) { /* fall through to drafting */ }
    // No instant fix — draft the ticket.
    const stages = ["Understanding the issue…", "Matching a service…", "Judging priority…", "Drafting the ticket…"];
    let si = 0; setAiStage(stages[0]);
    const iv = setInterval(() => { si = (si + 1) % stages.length; setAiStage(stages[si]); }, 900);
    try {
      const r = await api.aiTriage(incident);
      if (r?.draft) fillFromDraft(r.draft, incident);
    } catch (e) { toast.error(e.message || "AI could not draft this ticket"); }
    finally { clearInterval(iv); setAiBusy(false); setAiStage(""); }
  };

  const markSolved = () => { toast.success("Glad that fixed it! 🎉", { description: "No ticket needed." }); setOpen(false); setAiText(""); setAiAnswer(null); setAiResult(null); };
  const stillNeedHelp = async () => { setAiAnswer(null); await runTriage(); };

  const selectedCat = categories.find((c) => String(c.id) === String(cat));
  const canAnon = !!selectedCat?.allow_anonymous;
  const isConfidential = !!selectedCat?.is_confidential;
  const needsDept = !!selectedCat?.department_scoped;
  // Sub-categories come back with the category list, so newly added ones appear immediately.
  const subs = (selectedCat?.sub_categories || []).filter((sc) => sc.is_active !== false);
  const needsSub = subs.length > 0;
  const selectedSub = subs.find((sc) => String(sc.id) === String(subCat));
  const effectiveSla = selectedSub?.sla_hours || selectedCat?.sla_hours;

  // The actual ticket-creation call — identical to the original submit() body,
  // just extracted so both "raise anyway" and a clean (no-duplicate) submit
  // call the exact same code path. Nothing about this function changed.
  const createTicketNow = async () => {
    setBusy(true);
    try {
      const res = await api.createTicket({
        title: title.trim(),
        description: desc.trim() || "No additional details provided.",
        category_id: Number(cat),
        sub_category_id: needsSub ? Number(subCat) : undefined,
        priority: pr,
        location: location.trim() || undefined,
        department: needsDept ? department.trim() : undefined,
        anonymous: canAnon ? anonymous : undefined,
        anon_contact: canAnon && anonymous ? (anonContact.trim() || undefined) : undefined,
      });
      toast.success(`Ticket ${res?.ticket?.ticket_no || "created"}`, { description: "Auto-routing now." });
      // Optional image → uploaded to Google Drive by the backend and linked to the
      // ticket. Best-effort: a failed upload never blocks the ticket itself.
      if (image && res?.ticket?.id) {
        setImgUploading(true);
        try { await api.uploadAttachment(res.ticket.id, image); }
        catch (err) { toast.error(err.message || "Ticket created, but the image failed to upload."); }
        finally { setImgUploading(false); }
      }
      setOpen(false);
      setTitle(""); setDesc(""); setSubCat(""); setLocation(""); setDepartment(""); setPr("medium"); setAnonymous(false); setAnonContact(""); clearImage();
      setSimilarChecked(false); setSimilarMatches(null);
      onCreated?.(res?.ticket);
    } catch (e) {
      toast.error(e.message || "Could not create ticket");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Add a short title");
    if (!cat) return toast.error("Pick a category");
    if (needsDept && !department.trim()) return toast.error("Which department is this about?");
    if (needsSub && !subCat) return toast.error("Please choose a sub-category.");

    // ── Similar-ticket check (v8.1) — runs once per draft, right before
    // creating. v8.4 speed fix: capped to a 1s budget with Promise.race, so
    // a slow network or AI call can never make "click submit" feel stuck —
    // past that budget (or on any error) we fall straight through to
    // createTicketNow() exactly as before, unchanged.
    if (!similarChecked) {
      setSimilarBusy(true);
      let candidates = [];
      try {
        const r = await Promise.race([
          api.checkSimilarTickets({ title: title.trim(), description: desc.trim(), category_id: cat || undefined }),
          new Promise((_, reject) => setTimeout(() => reject(new Error("similar-check timeout")), 1000)),
        ]);
        candidates = r?.candidates || [];
      } catch { /* best-effort only — proceed to create */ }
      setSimilarBusy(false);
      setSimilarChecked(true);
      if (candidates.length) { setSimilarMatches(candidates); return; } // pause — let the user choose
    }
    await createTicketNow();
  };

  // "Track this instead" — subscribes the user to the existing ticket and
  // closes the dialog without creating a new ticket.
  const watchInstead = async (t) => {
    setWatchBusy(t.id);
    try {
      await api.watchTicket(t.id);
      toast.success(`You're now tracking ${t.ticket_no}`, { description: "We'll notify you as it's updated — no need to raise a new ticket." });
      setOpen(false);
      setTitle(""); setDesc(""); setSubCat(""); setLocation(""); setDepartment(""); setPr("medium"); setAnonymous(false); setAnonContact(""); clearImage();
      setSimilarChecked(false); setSimilarMatches(null);
    } catch (e) {
      toast.error(e.message || "Could not subscribe to that ticket");
    } finally { setWatchBusy(null); }
  };

  // "No, this is a different issue" — the check already ran (similarChecked
  // is true), so this just creates the ticket normally.
  const raiseAnyway = async () => {
    setSimilarMatches(null);
    await createTicketNow();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            data-testid="new-ticket-btn"
            className="rounded-full h-10 gap-1.5 bg-cobalt-500 hover:bg-cobalt-600 text-white shadow-[0_8px_24px_rgba(0,71,255,0.35)]"
          >
            <Plus className="h-4 w-4" /> New ticket
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] flex flex-col gap-0 p-0 rounded-2xl border-white/60 dark:border-white/10 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl" data-testid="new-ticket-dialog">
        {needsProfile ? (
          <>
            <DialogHeader className="p-6 pb-0 shrink-0">
              <div className="overline">One-time setup</div>
              <DialogTitle className="font-display text-3xl font-extrabold tracking-tighter">Complete your profile</DialogTitle>
              <DialogDescription>We need this once so tickets carry your student details. Takes a minute.</DialogDescription>
            </DialogHeader>
            <form onSubmit={savePf} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <div className="rounded-2xl border p-4 flex items-start gap-3 bg-cobalt-500/5 border-cobalt-500/20">
                  <GraduationCap className="h-5 w-5 text-cobalt-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    You'll only see this once — these details are saved to your account and reused on every ticket you raise after this.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pf-name">Student name</Label>
                  <Input id="pf-name" data-testid="pf-name" value={pf.name} onChange={setPfField("name")} className="rounded-xl" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="pf-usn">USN</Label>
                    <Input id="pf-usn" data-testid="pf-usn" placeholder="e.g. 1BY22CS045"
                      value={pf.usn} onChange={setPfField("usn")} className="rounded-xl uppercase" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pf-dept">Department</Label>
                    <Input id="pf-dept" data-testid="pf-dept" placeholder="e.g. CSE"
                      value={pf.department} onChange={setPfField("department")} className="rounded-xl" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Semester</Label>
                    <Select value={pf.semester} onValueChange={setPfField("semester")}>
                      <SelectTrigger data-testid="pf-semester" className="rounded-xl"><SelectValue placeholder="Pick semester" /></SelectTrigger>
                      <SelectContent>
                        {["1","2","3","4","5","6","7","8"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pf-section">Section</Label>
                    <Input id="pf-section" data-testid="pf-section" placeholder="e.g. A"
                      value={pf.section} onChange={setPfField("section")} className="rounded-xl uppercase" maxLength={5} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pf-phone">Mobile number</Label>
                  <Input id="pf-phone" data-testid="pf-phone" type="tel" placeholder="+91…"
                    value={pf.phone} onChange={setPfField("phone")} className="rounded-xl" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pf-email">Email</Label>
                  <Input id="pf-email" data-testid="pf-email" type="email"
                    value={pf.email} onChange={setPfField("email")} className="rounded-xl" />
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-border/60 shrink-0">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="pf-submit" disabled={pfBusy} className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
                  {pfBusy ? "Saving…" : "Save & continue"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
        <>
        <DialogHeader className="p-6 pb-0 shrink-0">
          <div className="overline">Raise a request</div>
          <DialogTitle className="font-display text-3xl font-extrabold tracking-tighter">What&apos;s the issue?</DialogTitle>
          <DialogDescription>Describe it in a line; add context below. We&apos;ll route it in under a minute.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {/* ✨ AI Assist — describe it, AI drafts the whole ticket */}
        <div className="relative overflow-hidden rounded-2xl p-[1.5px] bg-gradient-to-br from-cobalt-500 via-violet-500 to-fuchsia-500 shadow-[0_10px_40px_-12px_rgba(88,80,236,0.5)]">
          <div className="rounded-[15px] bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl p-4">
            <div className="flex items-center gap-2 mb-2.5">
              <span className={`relative flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-cobalt-500 to-violet-500 text-white ${aiBusy ? "animate-pulse" : ""}`}>
                <Sparkles className="h-4 w-4" />
                {aiBusy && <span className="absolute inset-0 rounded-full ring-2 ring-violet-400/60 animate-ping" />}
              </span>
              <div className="leading-tight">
                <div className="text-sm font-bold">Describe it — AI raises the ticket</div>
                <div className="text-[11px] text-muted-foreground">Type the problem in your own words; AI fills everything below.</div>
              </div>
            </div>
            <Textarea
              value={aiText} onChange={(e) => setAiText(e.target.value)}
              placeholder="e.g. Projector in Seminar Hall 2 won't switch on and I have a class at 2pm"
              rows={2}
              className="rounded-xl resize-none bg-white/70 dark:bg-white/5 border-violet-200/60 dark:border-violet-500/20"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); runAssist(); } }}
            />
            <div className="flex items-center justify-between mt-2.5">
              <div className="text-[11px] text-muted-foreground min-h-[16px]">
                {aiBusy ? <span className="inline-flex items-center gap-1.5 text-violet-600 dark:text-violet-400 font-medium"><Loader2 className="h-3 w-3 animate-spin" />{aiStage}</span>
                  : aiResult ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium"><Wand2 className="h-3 w-3" />Drafted below — review &amp; send</span>
                  : <span>⌘/Ctrl + Enter</span>}
              </div>
              <Button type="button" onClick={runAssist} disabled={aiBusy}
                className="rounded-full h-9 gap-1.5 bg-gradient-to-r from-cobalt-500 to-violet-500 hover:opacity-90 text-white text-sm px-4">
                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {aiBusy ? "Working…" : "Get instant help"}
              </Button>
            </div>

            {aiAnswer && (
              <div className="mt-3 rounded-xl border border-emerald-200/70 dark:border-emerald-500/25 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 animate-in fade-in slide-in-from-bottom-1 duration-300">
                <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 text-xs font-bold mb-1.5"><Wand2 className="h-3.5 w-3.5" />Instant fix — try this first</div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground/90">{aiAnswer}</p>
                {!!aiSources.length && <div className="flex flex-wrap gap-1 mt-2">{aiSources.map((s) => <span key={s.id} className="text-[10px] px-2 py-0.5 rounded-full bg-white/70 dark:bg-white/10 text-muted-foreground">{s.title}</span>)}</div>}
                <div className="flex gap-2 mt-3">
                  <Button type="button" size="sm" onClick={markSolved} className="rounded-full h-8 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">✅ This solved it</Button>
                  <Button type="button" size="sm" variant="outline" onClick={stillNeedHelp} className="rounded-full h-8 gap-1.5">🎫 Still need help</Button>
                </div>
              </div>
            )}

            {aiResult && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5 animate-in fade-in slide-in-from-bottom-1 duration-300">
                {aiResult.catName && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cobalt-500/10 text-cobalt-600 dark:text-cobalt-400">{aiResult.catName}{aiResult.subName ? " · " + aiResult.subName : ""}</span>}
                {aiResult.priority && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${aiResult.priority === "urgent" ? "bg-red-500/10 text-red-600" : aiResult.priority === "high" ? "bg-orange-500/10 text-orange-600" : aiResult.priority === "low" ? "bg-slate-500/10 text-slate-500" : "bg-amber-500/10 text-amber-600"}`}>{aiResult.priority} priority</span>}
                {typeof aiResult.confidence === "number" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{Math.round(aiResult.confidence * 100)}% confident</span>}
                {aiResult.reasoning && <span className="text-[11px] text-muted-foreground w-full mt-0.5">💡 {aiResult.reasoning}</span>}
              </div>
            )}
          </div>
        </div>

        <div className="relative my-1 flex items-center gap-3">
          <span className="h-px flex-1 bg-border" />
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">or fill in manually</span>
          <span className="h-px flex-1 bg-border" />
        </div>

          <div className="space-y-1.5">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              data-testid="new-ticket-title"
              placeholder="e.g. Projector won&apos;t turn on — Seminar Hall 2"
              value={title} onChange={(e) => setTitle(e.target.value)}
              className="rounded-xl"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={cat} onValueChange={(v) => { setCat(v); setSubCat(""); }}>
                <SelectTrigger data-testid="new-ticket-category" className="rounded-xl">
                  <SelectValue placeholder={categories.length ? "Pick service" : "Loading…"} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {needsSub && (
              <div className="space-y-1.5">
                <Label>Sub-category</Label>
                <Select value={subCat} onValueChange={setSubCat}>
                  <SelectTrigger data-testid="new-ticket-subcategory" className="rounded-xl">
                    <SelectValue placeholder="Pick the specific issue" />
                  </SelectTrigger>
                  <SelectContent>
                    {subs.map((sc) => <SelectItem key={sc.id} value={String(sc.id)}>{sc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {selectedSub?.description && (
                  <p className="text-[11px] text-muted-foreground">{selectedSub.description}</p>
                )}
              </div>
            )}
            {effectiveSla && (
              <p className="text-[11px] text-muted-foreground sm:col-span-2">
                Target response: within <b>{effectiveSla} hours</b>.
              </p>
            )}
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={pr} onValueChange={setPr}>
                <SelectTrigger data-testid="new-ticket-priority" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(PRIORITY).map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="loc">Location <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              id="loc"
              data-testid="new-ticket-location"
              placeholder="e.g. Seminar Hall 2, Block A"
              value={location} onChange={(e) => setLocation(e.target.value)}
              className="rounded-xl"
            />
          </div>
          {needsDept && (
            <div className="space-y-1.5">
              <Label htmlFor="dept">Which department is this about?</Label>
              <Input
                id="dept"
                data-testid="new-ticket-department"
                placeholder="e.g. Computer Science"
                value={department} onChange={(e) => setDepartment(e.target.value)}
                className="rounded-xl"
              />
              <p className="text-xs text-muted-foreground">This routes your complaint to that department's HOD.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="desc">Details</Label>
            <Textarea
              id="desc"
              data-testid="new-ticket-desc"
              placeholder="Room number, time noticed, anything you tried…"
              value={desc} onChange={(e) => setDesc(e.target.value)}
              rows={4}
              className="rounded-xl"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Photo <span className="text-muted-foreground">(optional)</span></Label>
            {!imagePreview ? (
              <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed h-20 cursor-pointer text-sm text-muted-foreground hover:border-cobalt-400 hover:text-cobalt-500 transition-colors">
                <ImageUp className="h-4 w-4" /> Attach a photo or screenshot
                <input type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="new-ticket-image" />
              </label>
            ) : (
              <div className="relative w-fit">
                <img src={imagePreview} alt="Attachment preview" className="h-20 w-20 rounded-xl object-cover border" />
                <button type="button" onClick={clearImage}
                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-zinc-900 text-white flex items-center justify-center">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Paperclip className="h-3 w-3" /> Stored in Google Drive · PNG/JPG/GIF/WEBP · up to 2 MB
            </p>
          </div>

          {isConfidential && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 dark:bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
              This is a confidential category. Your report is routed privately to the responsible authority and is not visible to general administrators or technicians.
            </div>
          )}

          {canAnon && (
            <div className="rounded-xl border p-3 space-y-2.5">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)}
                  data-testid="anon-toggle" className="h-4 w-4 rounded accent-cobalt-500" />
                <span className="text-sm font-medium">Report anonymously</span>
              </label>
              {anonymous && (
                <div className="space-y-1.5 pl-6.5">
                  <p className="text-xs text-muted-foreground">
                    Your identity will not be attached to this report. Optionally, provide an email if you want status updates — leave blank to stay fully anonymous.
                  </p>
                  <Input type="email" placeholder="Email for updates (optional)"
                    value={anonContact} onChange={(e) => setAnonContact(e.target.value)}
                    className="rounded-xl" data-testid="anon-contact" />
                </div>
              )}
            </div>
          )}

          {/* ── Similar-ticket check (v8.1) — shown only when check-similar found
              close matches; otherwise this renders nothing and the form behaves
              exactly as before. ── */}
          {similarBusy && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking for similar open tickets…
            </div>
          )}
          {similarMatches?.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 dark:bg-amber-500/5 p-4 space-y-3 animate-in fade-in slide-in-from-bottom-1 duration-300" data-testid="similar-tickets-panel">
              <div className="text-sm font-bold text-amber-800 dark:text-amber-400">This looks like an existing report</div>
              <p className="text-xs text-muted-foreground">
                Subscribing instead means we notify you as it progresses — one ticket, one fix, faster for everyone.
              </p>
              <div className="space-y-2">
                {similarMatches.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 rounded-xl bg-white/70 dark:bg-white/5 border p-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{t.title}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {t.ticket_no} · {t.status}{t.watcher_count > 0 ? ` · ${t.watcher_count} other${t.watcher_count === 1 ? "" : "s"} tracking this` : ""}
                      </div>
                    </div>
                    <Button type="button" size="sm" disabled={watchBusy === t.id}
                      onClick={() => watchInstead(t)}
                      className="rounded-full h-8 shrink-0 bg-amber-600 hover:bg-amber-700 text-white">
                      {watchBusy === t.id ? "Subscribing…" : "Track this instead"}
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={raiseAnyway} disabled={busy}
                className="rounded-full h-8 w-full">
                {busy ? "Creating…" : "No, this is a different issue — raise a new ticket"}
              </Button>
            </div>
          )}
        </div>
          <DialogFooter className="px-6 py-4 border-t border-border/60 shrink-0">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="new-ticket-submit" disabled={busy || imgUploading || similarBusy} className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
              {busy ? "Creating…" : imgUploading ? "Uploading photo…" : similarBusy ? "Checking…" : "Raise ticket"}
            </Button>
          </DialogFooter>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
